import { useState, useEffect, useCallback } from "react";
import PanelRefreshButton from "./PanelRefreshButton";
import { API, axios } from "@/api";
import {
  Activity, Check, Pencil, ShieldCheck, Bell, Bug, Loader2, RefreshCw,
  Undo2, ChevronDown, ChevronUp,
} from "lucide-react";

const ACTION_ICONS = {
  mark_verified: { icon: ShieldCheck, color: "text-green-500", bg: "bg-green-50" },
  edit_fields: { icon: Pencil, color: "text-sky-500", bg: "bg-sky-50" },
  batch_mark_verified: { icon: Check, color: "text-green-500", bg: "bg-green-50" },
  batch_dismissed_alerts: { icon: Bell, color: "text-slate-500", bg: "bg-slate-50" },
  batch_snoozed_alerts: { icon: Bell, color: "text-amber-500", bg: "bg-amber-50" },
  batch_acknowledge_bugs: { icon: Bug, color: "text-sky-500", bg: "bg-sky-50" },
  batch_dismiss_bugs: { icon: Bug, color: "text-slate-500", bg: "bg-slate-50" },
  batch_fix_shipped_bugs: { icon: Bug, color: "text-green-500", bg: "bg-green-50" },
  batch_validate_bugs: { icon: Bug, color: "text-emerald-500", bg: "bg-emerald-50" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function actionLabel(action) {
  const labels = {
    mark_verified: "Verified",
    edit_fields: "Edited",
    batch_mark_verified: "Batch Verified",
    batch_dismissed_alerts: "Dismissed Alerts",
    batch_snoozed_alerts: "Snoozed Alerts",
    batch_acknowledge_bugs: "Ack'd Bugs",
    batch_dismiss_bugs: "Dismissed Bugs",
    batch_fix_shipped_bugs: "Shipped Fix",
    batch_validate_bugs: "Validated Bugs",
  };
  return labels[action] || action.replace(/_/g, " ");
}

export default function ActivityFeedPanel({ compact = false }) {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(!compact);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const limit = compact ? 8 : 25;

  const fetchActions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/actions?limit=${limit}`);
      setActions(res.data);
      setLastRefreshedAt(Date.now());
    } catch {
      setActions([]);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchActions();
    const interval = setInterval(fetchActions, 30000);
    return () => clearInterval(interval);
  }, [fetchActions]);

  if (compact && !expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="w-full px-4 py-2.5 flex items-center gap-2 text-xs font-semibold text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition"
        data-testid="activity-feed-expand"
      >
        <Activity className="w-3.5 h-3.5" />
        <span className="flex-1 text-left">Activity Feed</span>
        {actions.length > 0 && (
          <span className="w-5 h-5 rounded-full bg-sky-100 text-sky-600 text-[10px] font-bold flex items-center justify-center">
            {actions.length}
          </span>
        )}
        <ChevronDown className="w-3 h-3" />
      </button>
    );
  }

  return (
    <div className="space-y-2" data-testid="activity-feed-panel">
      <div className="flex justify-end -mb-1"><PanelRefreshButton onRefresh={fetchActions} lastRefreshedAt={lastRefreshedAt} testId="activity-feed-refresh" /></div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="w-3.5 h-3.5 text-sky-500" />
          <span className="text-xs font-bold text-slate-700">Activity Feed</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={fetchActions}
            className="p-1 rounded hover:bg-slate-100 transition"
            data-testid="activity-feed-refresh"
          >
            <RefreshCw className={`w-3 h-3 text-slate-400 ${loading ? "animate-spin" : ""}`} />
          </button>
          {compact && (
            <button
              onClick={() => setExpanded(false)}
              className="p-1 rounded hover:bg-slate-100 transition"
              data-testid="activity-feed-collapse"
            >
              <ChevronUp className="w-3 h-3 text-slate-400" />
            </button>
          )}
        </div>
      </div>

      {loading && actions.length === 0 ? (
        <div className="flex justify-center py-3">
          <Loader2 className="w-4 h-4 animate-spin text-slate-300" />
        </div>
      ) : actions.length === 0 ? (
        <p className="text-[10px] text-slate-400 text-center py-3">No actions yet</p>
      ) : (
        <div className="space-y-1 max-h-72 overflow-y-auto">
          {actions.map((a) => {
            const cfg = ACTION_ICONS[a.action] || { icon: Activity, color: "text-slate-500", bg: "bg-slate-50" };
            const Icon = cfg.icon;
            const isUndone = a.undone;

            return (
              <div
                key={a.id}
                className={`flex items-start gap-2 px-2 py-1.5 rounded-lg transition ${isUndone ? "opacity-40" : "hover:bg-slate-50"}`}
                data-testid={`activity-item-${a.id}`}
              >
                <div className={`w-5 h-5 rounded-md ${cfg.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                  <Icon className={`w-2.5 h-2.5 ${cfg.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-slate-700 leading-tight">
                    <span className="font-semibold">{actionLabel(a.action)}</span>
                    {" "}
                    <span className="text-slate-500">{a.target_name}</span>
                    {isUndone && (
                      <span className="ml-1 text-[9px] text-red-400 font-medium flex-shrink-0 inline-flex items-center gap-0.5">
                        <Undo2 className="w-2 h-2" /> undone
                      </span>
                    )}
                  </p>
                  <p className="text-[9px] text-slate-400 mt-0.5">
                    {a.admin_name} · {timeAgo(a.created_at)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
